import { useEffect, useRef, useState } from "react"

import parse from "html-react-parser"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import {
  Brain,
  Check,
  CheckCircle2,
  ClipboardList,
  Copy,
  FileText,
  List,
  MessageSquareText,
  Sparkles,
  Target,
} from "@/components/icons"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { buttonVariants } from "@/components/ui/button-variants"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { CourseLayout } from "@/features/training/components/layouts/course-layout"
import { CourseSection } from "@/features/training/components/layouts/course-section"
import { ensureArray } from "@/features/training/utils/ensure-array"
import { cn } from "@/lib/utils"

const QUICK_TIPS = [
  { icon: Target, key: "be_specific" },
  { icon: FileText, key: "give_context" },
  { icon: MessageSquareText, key: "use_examples" },
  { icon: List, key: "step_by_step" },
  { icon: Sparkles, key: "positive_phrasing" },
  { icon: Brain, key: "think_first" },
] as const

export function PromptEngineeringPage() {
  const { t } = useTranslation("", { keyPrefix: "training.prompting.content" })
  const { t: tTips } = useTranslation("", { keyPrefix: "training.prompting.quick_tips" })
  const checklistItems = ensureArray<string>(
    t("checklist_items", { returnObjects: true, defaultValue: [] })
  )
  const [copied, setCopied] = useState(false)
  const resetCopiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (resetCopiedTimerRef.current !== null) {
        clearTimeout(resetCopiedTimerRef.current)
      }
    }
  }, [])

  const handleCopyExercise = async () => {
    try {
      await navigator.clipboard.writeText(t("exercise_prompt"))
      setCopied(true)
      toast.success(t("exercise_copy_success"))
      if (resetCopiedTimerRef.current !== null) {
        clearTimeout(resetCopiedTimerRef.current)
      }
      resetCopiedTimerRef.current = setTimeout(() => {
        setCopied(false)
        resetCopiedTimerRef.current = null
      }, 2000)
    } catch {
      toast.error(t("exercise_copy_error", "Could not copy — please select and copy manually."))
    }
  }

  return (
    <CourseLayout>
      <CourseSection>
        <h2> {t("prompting")}</h2>
        <p>{parse(t("intro_text_0"))}</p>
        <p>{parse(t("intro_text_1"))}</p>
        <p>{parse(t("intro_text_2", ""))}</p>
        <img
          alt="Description"
          className="mx-auto w-full rounded-[6px] md:max-w-[90%]"
          src="/assets/training_images/challenges/input_output.png"
        />
      </CourseSection>
      <CourseSection className="gap-8">
        <h2> {t("prompt_eng")}</h2>
        <CourseSection>
          <h3>{t("clarity_specificity_header")}</h3>
          <p>{parse(t("clarity_specificity_text"))}</p>
        </CourseSection>
        <CourseSection>
          <h3>{t("context_examples_header")}</h3>
          <p>{parse(t("context_examples_text"))}</p>
        </CourseSection>

        <CourseSection>
          <h3>{t("specific_formats_header")}</h3>
          <p>{parse(t("specific_formats_text"))}</p>
        </CourseSection>
        <CourseSection>
          <h3>{t("tone_style_audience_header")}</h3>
          <p>{parse(t("tone_style_audience_text"))}</p>
        </CourseSection>
        <CourseSection>
          <h3>{t("experiment_iterate_header")}</h3>
          <p>{parse(t("experiment_iterate_text"))}</p>
        </CourseSection>
        <CourseSection>
          <h3>{t("positive_instructions_header")}</h3>
          <p>{parse(t("positive_instructions_text"))}</p>
        </CourseSection>
        <CourseSection>
          <h3>{t("chain_of_thought_header")}</h3>
          <p>{parse(t("chain_of_thought_text"))}</p>
        </CourseSection>
        <CourseSection>
          <h3>{t("few_shot_header")}</h3>
          <p>{parse(t("few_shot_text"))}</p>
        </CourseSection>
        <CourseSection>
          <h3>{t("beyond_baseline_header")}</h3>
          <p>{parse(t("beyond_baseline_text"))}</p>
        </CourseSection>
        <CourseSection>
          <Accordion collapsible className="w-full" type="single">
            <AccordionItem value="item-1">
              <AccordionTrigger>
                <h3> {t("example_header")}</h3>
              </AccordionTrigger>
              <AccordionContent>
                <p>{parse(t("examples_prompts"))}</p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CourseSection>
      </CourseSection>

      {checklistItems.length > 0 && (
        <CourseSection>
          <Card className="border-success bg-success/15">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <ClipboardList className="text-success h-5 w-5" />
                <h3 className="text-success text-base font-bold">{t("checklist_title")}</h3>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2.5">
                {checklistItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="text-success mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </CourseSection>
      )}

      <CourseSection>
        <Card className="border-primary/30 bg-primary/5">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="text-primary h-5 w-5" />
              <h3 className="text-primary text-base font-bold">{t("exercise_title")}</h3>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <p className="text-sm">{t("exercise_intro")}</p>
            <div className="relative">
              <pre className="bg-background overflow-x-auto rounded-lg border p-3 pr-12 text-sm whitespace-pre-wrap">
                {t("exercise_prompt")}
              </pre>
              <Tooltip>
                <TooltipTrigger
                  aria-label={t("exercise_copy_label")}
                  type="button"
                  className={cn(
                    buttonVariants({
                      size: "icon",
                      variant: "icon",
                      className: "absolute top-2 right-2 size-8 hover:bg-transparent",
                    })
                  )}
                  onClick={handleCopyExercise}
                >
                  {copied ? (
                    <Check className="text-success size-4" />
                  ) : (
                    <Copy className="text-muted-foreground size-4" />
                  )}
                </TooltipTrigger>
                <TooltipContent side="top" sideOffset={5}>
                  <p>{t("exercise_copy_label")}</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <p className="text-muted-foreground text-sm">{t("exercise_outro")}</p>
          </CardContent>
        </Card>
      </CourseSection>

      {/* Quick Tips from Fact Sheet */}
      <CourseSection>
        <h2>{tTips("title", "Quick Tips for Better Answers")}</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {QUICK_TIPS.map(({ icon: Icon, key }) => (
            <Card key={key}>
              <CardContent className="flex items-start gap-3 p-4">
                <div className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold">{tTips(`${key}.title`)}</p>
                  <p className="text-muted-foreground text-xs">{tTips(`${key}.description`)}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CourseSection>
    </CourseLayout>
  )
}
